import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  BarChart3,
  ExternalLink,
  LineChart,
  Users,
  Globe,
  MousePointer
} from 'lucide-react';

const ProjectAnalytics = ({ project }) => {
  // Extract domain from website URL
  const getDomain = () => {
    if (!project?.website_url) return null;
    try {
      let domain = project.website_url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0];
      return domain;
    } catch {
      return null;
    }
  };

  const domain = getDomain();
  const analyticsUrl = domain ? `https://a2ganalytics.com/${domain}` : null;

  const openAnalytics = () => {
    if (analyticsUrl) {
      window.open(analyticsUrl, 'a2ganalytics', 'width=1400,height=900,scrollbars=yes,resizable=yes');
    }
  };

  if (!domain) {
    return (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 size={20} />
            Website Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Website URL</h3>
            <p className="text-slate-500">
              Add a website URL to this project to view analytics.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 size={20} />
            Website Analytics
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-center py-12">
          {/* Analytics Icon */}
          <div className="flex justify-center gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-full">
              <LineChart size={32} className="text-blue-600" />
            </div>
            <div className="p-4 bg-emerald-50 rounded-full">
              <Users size={32} className="text-emerald-600" />
            </div>
            <div className="p-4 bg-purple-50 rounded-full">
              <Globe size={32} className="text-purple-600" />
            </div>
            <div className="p-4 bg-amber-50 rounded-full">
              <MousePointer size={32} className="text-amber-600" />
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            View Analytics for {domain}
          </h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto">
            Track pageviews, visitors, traffic sources, devices, locations, and more with a2ganalytics.
          </p>
          
          <div className="flex justify-center gap-3">
            <Button 
              size="lg" 
              onClick={openAnalytics}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <BarChart3 size={18} className="mr-2" />
              Open Analytics Dashboard
            </Button>
            <a href={analyticsUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline">
                <ExternalLink size={18} className="mr-2" />
                Open in New Tab
              </Button>
            </a>
          </div>
          
          <p className="text-xs text-slate-400 mt-6">
            Powered by <a href="https://a2ganalytics.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">a2ganalytics.com</a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectAnalytics;
